import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

const tiers = [
  {
    id: "small",
    name: "Mindre reparasjoner",
    intro: "Raske inngrep som ofte fullføres samme dag etter ankomst.",
    items: [
      "Batteribytte",
      "Ladeport",
      "Knapper og brytere",
    ],
    testId: "pricing-tier-1",
  },
  {
    id: "screen",
    name: "Skjerm og glass",
    intro: "Kvalitetssikrede skjermer med full funksjonalitet.",
    items: [
      "iPhone-skjerm og glass",
      "iPad-glass og digitizer",
      "MacBook-skjermbytte",
      "iMac-skjermpanel",
    ],
    testId: "pricing-tier-2",
    highlighted: true,
  },
  {
    id: "complex",
    name: "Avanserte reparasjoner",
    intro: "Komplekse inngrep som krever mikrolodding eller spesialverktøy.",
    items: [
      "Logikkort og mikrolodding",
      "Væskeskade og rens",
      "SSD- og minneoppgradering",
    ],
    testId: "pricing-tier-3",
  },
];

export default function Pricing() {
  const scrollToContact = () => {
    const el = document.getElementById("kontakt");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="priser"
      className="py-24 md:py-32 bg-[#F5F5F7]"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-xs uppercase tracking-[0.18em] text-[#86868B]">
            Priser
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F]">
            Tydelig pristilbud før vi setter i gang.
          </h2>
          <p className="mt-5 text-lg text-[#1D1D1F]/70 leading-relaxed">
            Vi gir deg et uforpliktende pristilbud basert på diagnosen. Du
            bestemmer om reparasjonen skal utføres — alltid uten skjulte gebyrer.
          </p>
        </div>

        {/* Diagnose — the only item with a fixed price */}
        <div
          className="mt-12 rounded-3xl bg-white border border-black/[0.06] p-7 md:p-9 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
          data-testid="pricing-diagnose"
        >
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.18em] text-[#86868B]">
              Diagnose
            </div>
            <h3 className="font-display mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-[#1D1D1F]">
              Vi finner feilen — alltid til fast pris.
            </h3>
            <p className="mt-3 text-[15px] md:text-base text-[#1D1D1F]/70 leading-relaxed max-w-2xl">
              Diagnoseavgiften dekker en grundig vurdering av enheten. Du får
              beskjed om reparasjonskostnaden før vi setter i gang — ingen
              arbeid utføres uten din godkjenning.
            </p>
          </div>
          <div className="flex md:flex-col md:items-end items-center justify-between md:justify-center gap-4 md:gap-3 md:min-w-[200px]">
            <div className="text-right">
              <div
                className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F]"
                data-testid="pricing-diagnose-amount"
              >
                890 kr
              </div>
              <div className="text-xs text-[#86868B] mt-1">Fast pris</div>
            </div>
            <Button
              onClick={scrollToContact}
              className="rounded-full bg-[#1D1D1F] hover:bg-[#0a0a0a] text-white h-11 px-6 text-sm font-medium"
              data-testid="pricing-diagnose-cta"
            >
              Bestill diagnose
            </Button>
          </div>
        </div>

        <div className="mt-10">
          <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight text-[#1D1D1F]">
            Reparasjonskategorier
          </h3>
          <p className="mt-2 text-sm text-[#86868B] max-w-2xl">
            Prisen for selve reparasjonen avhenger av modell og feil. Du får et
            fast tilbud etter diagnosen.
          </p>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          {tiers.map((t) => (
            <article
              key={t.id}
              className={`rounded-3xl p-8 border transition-all duration-300 hover:-translate-y-1 ${
                t.highlighted
                  ? "bg-[#1D1D1F] text-white border-[#1D1D1F]"
                  : "bg-white text-[#1D1D1F] border-black/[0.05] hover:shadow-[0_18px_60px_-20px_rgba(0,0,0,0.10)]"
              }`}
              data-testid={t.testId}
            >
              <h3 className="font-display text-2xl font-semibold tracking-tight">
                {t.name}
              </h3>
              <p
                className={`mt-2 text-sm leading-relaxed ${
                  t.highlighted ? "text-white/70" : "text-[#86868B]"
                }`}
              >
                {t.intro}
              </p>

              <ul className="mt-7 space-y-3">
                {t.items.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-sm">
                    <Check
                      size={16}
                      strokeWidth={2}
                      className={
                        t.highlighted ? "text-white mt-0.5" : "text-[#1D1D1F] mt-0.5"
                      }
                    />
                    <span className={t.highlighted ? "text-white/90" : "text-[#1D1D1F]/80"}>
                      {it}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={scrollToContact}
                className={`mt-8 w-full rounded-full h-11 text-sm font-medium ${
                  t.highlighted
                    ? "bg-white text-[#1D1D1F] hover:bg-white/90"
                    : "bg-[#1D1D1F] text-white hover:bg-[#0a0a0a]"
                }`}
                data-testid={`${t.testId}-cta`}
              >
                Be om pristilbud
              </Button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
